import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ChevronRight } from "lucide-react";
import api from "@/lib/api";
import { useSite } from "@/contexts/SiteContext";
import { RenderIcon } from "@/components/admin/IconPicker";
import SectionCta from "@/components/SectionCta";

/**
 * Home "Cu ce te pot ajuta" section.
 *
 * PRIMARY SOURCE: db.problems collection — each item is an indexable page at /vindecare/{slug}.
 * FALLBACK: legacy sections.problems.content.items (flat chip list — non-clickable).
 *
 * Cards: thumbnail + name + summary + "Află mai mult" → /vindecare/{slug}.
 */
export default function Problems() {
  const { sectionContent } = useSite();
  const c = sectionContent("problems");
  const [problems, setProblems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/public/problems")
      .then((r) => setProblems(Array.isArray(r.data) ? r.data : []))
      .catch(() => setProblems([]))
      .finally(() => setLoaded(true));
  }, []);

  if (!c && !loaded) return null;
  const homeProblems = problems.filter((p) => p.show_on_home !== false);
  const useCollection = loaded && homeProblems.length > 0;
  const legacyChips = (c?.items || []);

  return (
    <section data-testid="problems-section" className="py-14 sm:py-20 lg:py-28" style={{ background: "var(--ct-bg)" }}>
      <div className="max-w-7xl mx-auto px-5 lg:px-8">
        <div className="text-center mb-8 sm:mb-12 lg:mb-14 max-w-3xl mx-auto">
          <div className="flex items-center justify-center gap-4 mb-3 sm:mb-4">
            <span className="gold-rule" />
            <span data-el="eyebrow" data-field="eyebrow" className="eyebrow">{c?.eyebrow || "Cu ce te pot ajuta"}</span>
            <span className="gold-rule" />
          </div>
          <h2 data-el="title" data-field="title" className="font-serif text-3xl sm:text-4xl lg:text-5xl leading-tight">{c?.title || "Cu ce te pot ajuta"}</h2>
          {c?.intro && <p data-el="intro" data-field="intro" className="text-sm sm:text-base leading-relaxed mt-3 sm:mt-4" style={{ color: "var(--ct-text-muted)" }}>{c.intro}</p>}
        </div>

        {useCollection ? (
          <>
            {/* MOBILE: compact rows */}
            <div className="flex flex-col gap-3 sm:hidden">
              {homeProblems.map((p, i) => (
                <Link
                  to={`/vindecare/${p.slug}`}
                  key={p.id}
                  data-testid={`problem-card-mobile-${i}`}
                  className="card-premium overflow-hidden grid grid-cols-[35%_1fr] gap-0 active:scale-[0.99] transition-transform"
                >
                  <div className="relative overflow-hidden flex items-center justify-center" style={{ background: "var(--ct-cream-edge)" }}>
                    {p.thumbnail_url || p.image_url ? (
                      <img src={p.thumbnail_url || p.image_url} alt={p.name} className="w-full h-full object-cover" />
                    ) : (
                      <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ background: "var(--ct-emerald)" }}>
                        <RenderIcon value={p.icon || "Sparkles"} size={16} color="var(--ct-gold)" />
                      </div>
                    )}
                  </div>
                  <div className="p-3.5 flex flex-col justify-center">
                    <h3 className="font-serif text-[17px] leading-tight mb-1">{p.headline || p.name}</h3>
                    <p className="text-[12px] leading-snug line-clamp-3" style={{ color: "var(--ct-text-muted)" }}>{p.summary}</p>
                    <span className="mt-2 inline-flex items-center gap-1.5 text-[11px] font-medium" style={{ color: "var(--ct-link)" }}>
                      {p.home_cta_label || "Află mai mult"} <ChevronRight size={12} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>

            {/* DESKTOP: 2x4 grid */}
            <div className="hidden sm:grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
              {homeProblems.map((p, i) => (
                <Link
                  to={`/vindecare/${p.slug}`}
                  key={p.id}
                  data-testid={`problem-card-${i}`}
                  className="card-premium overflow-hidden flex flex-col h-full hover:-translate-y-0.5 transition-transform group"
                >
                  <div className="aspect-[4/3] overflow-hidden relative" style={{ background: "var(--ct-cream-edge)" }}>
                    {p.thumbnail_url || p.image_url ? (
                      <img src={p.thumbnail_url || p.image_url} alt={p.name} className="w-full h-full object-cover transition-transform group-hover:scale-105" />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center">
                        <div className="w-14 h-14 rounded-full flex items-center justify-center" style={{ background: "var(--ct-emerald)", border: "1.5px solid var(--ct-gold)" }}>
                          <RenderIcon value={p.icon || "Sparkles"} size={22} color="var(--ct-gold)" />
                        </div>
                      </div>
                    )}
                  </div>
                  <div className="p-5 flex flex-col flex-1">
                    <h3 className="font-serif text-xl leading-tight mb-2">{p.headline || p.name}</h3>
                    <p className="text-sm leading-relaxed flex-1" style={{ color: "var(--ct-text-muted)" }}>{p.summary}</p>
                    <span
                      className="mt-4 inline-flex items-center gap-2 text-sm font-medium group-hover:gap-3 transition-all"
                      style={{ color: "var(--ct-link)" }}
                      data-testid={`problem-cta-${i}`}
                    >
                      {p.home_cta_label || "Află mai mult"} <ChevronRight size={14} />
                    </span>
                  </div>
                </Link>
              ))}
            </div>
          </>
        ) : (
          /* Legacy chip layout (only if collection empty) */
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {legacyChips.map((p, i) => (
              <div
                key={i}
                data-testid={`problem-item-${i}`}
                className="px-5 py-4 card-premium text-sm tracking-wide text-center"
                style={{ color: "var(--ct-text)" }}
              >
                {p}
              </div>
            ))}
          </div>
        )}

        {/* Buton de contact opțional sub grila de carduri (cta-spec.md secțiunea
            G.2) — clientul a cerut „dacă vreau", deci implicit OPRIT
            (cta_enabled: false în seed/DB); SectionCta randează null în acel caz. */}
        <SectionCta content={c || {}} prefix="cta" fallbackText="Scrie-mi acum" fallbackLink="/programare" className="mt-8 sm:mt-12 text-center" />

        {/* Harta Vindecării: puntea spre Barometru + Camera SOS — condiționată de
            content.show_bridges (implicit oprit; ambele module sunt dezactivate
            și nu apar în documentele de brand ale clientului). Cf. show_channels
            din Hero.jsx / CtaBand.jsx: gata de reactivat din Live Editor, fără dev. */}
        {c?.show_bridges && (
          <div className="mt-8 sm:mt-12 grid sm:grid-cols-2 gap-4 max-w-3xl mx-auto" data-testid="problems-bridge">
            <Link
              to="/test"
              className="card-premium p-5 sm:p-6 text-center hover:-translate-y-0.5 transition-transform"
              data-testid="bridge-barometru"
            >
              <p data-field="bridge1_title" className="font-serif text-lg sm:text-xl mb-1.5">{c?.bridge1_title || "Nu știi de unde să începi?"}</p>
              <p data-field="bridge1_body" className="text-xs sm:text-sm mb-3" style={{ color: "var(--ct-text-muted)" }}>
                {c?.bridge1_body || "Fă testul de 3 minute și află ce te apasă cel mai tare — și care e primul pas potrivit ție."}
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--ct-link)" }}>
                <span data-field="bridge1_cta">{c?.bridge1_cta || "Barometrul Interior"}</span> <ChevronRight size={14} />
              </span>
            </Link>
            <Link
              to="/sos"
              className="card-premium p-5 sm:p-6 text-center hover:-translate-y-0.5 transition-transform"
              style={{ borderColor: "var(--ct-gold)" }}
              data-testid="bridge-sos"
            >
              <p data-field="bridge2_title" className="font-serif text-lg sm:text-xl mb-1.5">{c?.bridge2_title || "Îți e greu chiar acum?"}</p>
              <p data-field="bridge2_body" className="text-xs sm:text-sm mb-3" style={{ color: "var(--ct-text-muted)" }}>
                {c?.bridge2_body || "Camera SOS e deschisă non-stop: respirație ghidată, ajutor imediat, oameni la telefon. Gratuit, fără cont."}
              </p>
              <span className="inline-flex items-center gap-2 text-sm font-medium" style={{ color: "var(--ct-link)" }}>
                <span data-field="bridge2_cta">{c?.bridge2_cta || "Intră în Camera SOS"}</span> <ChevronRight size={14} />
              </span>
            </Link>
          </div>
        )}
      </div>
    </section>
  );
}
